import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { classifyParameterC } from './JuliaParameterExplorer';
import { findJuliaAttractingCycle, iterateUntilEscape } from './fractalIteration';
import { EXPLORER_PALETTE, getPaletteTable, PALETTE_SIZE, samplePalette, withPaletteTheme } from './fractalPalette';

const PAIR_CANVAS_SIZE = 480;
const ZOOM_CANVAS_WIDTH = 720;
const ZOOM_CANVAS_HEIGHT = 450;

function cmul([ar, ai], [br, bi]) {
  return [ar * br - ai * bi, ar * bi + ai * br];
}

function cdiv([ar, ai], [br, bi]) {
  const denom = br * br + bi * bi || 1e-30;
  return [(ar * br + ai * bi) / denom, (ai * br - ar * bi) / denom];
}

export function computeKoenigsInvariants([cx, cy], arms = 2) {
  const dx = 1 - 4 * cx;
  const dy = -4 * cy;
  const r = Math.hypot(dx, dy);
  const sx = Math.sqrt((r + dx) / 2);
  const sy = (dy >= 0 ? 1 : -1) * Math.sqrt(Math.max(0, (r - dx) / 2));
  const alpha = [(1 - sx) / 2, -sy / 2];
  const lambda = [2 * alpha[0], 2 * alpha[1]];
  const mag1 = Math.hypot(lambda[0], lambda[1]);
  const arg1 = Math.atan2(lambda[1], lambda[0]);

  let lambdaM = [1, 0];
  for (let k = 0; k < arms; k++) {
    lambdaM = cmul(lambdaM, lambda);
  }
  const magM = Math.hypot(lambdaM[0], lambdaM[1]);
  const argM = Math.atan2(lambdaM[1], lambdaM[0]);

  const lambdaMinus1 = [lambda[0] - 1, lambda[1]];
  const a2 = cdiv([1, 0], cmul(lambda, lambdaMinus1));
  const lambdaSq = cmul(lambda, lambda);
  const lambdaSqMinus1 = [lambdaSq[0] - 1, lambdaSq[1]];
  const a3 = cdiv([2 * a2[0], 2 * a2[1]], cmul(lambda, lambdaSqMinus1));

  return {
    c: [cx, cy],
    arms,
    alpha,
    lambda,
    mag1,
    arg1,
    arg1Deg: (arg1 * 180) / Math.PI,
    lambdaM,
    magM,
    argM,
    argMDeg: (argM * 180) / Math.PI,
    a2,
    a3,
  };
}

function interpolateCatmullRom(points, t) {
  const clamped = Math.max(0, Math.min(1, t));
  const n = points.length - 1;
  if (n <= 0) return points[0] || [0, 0];
  const scaled = clamped * n;
  const idx = Math.min(n - 1, Math.floor(scaled));
  const u = scaled - idx;
  const p0 = points[Math.max(0, idx - 1)];
  const p1 = points[idx];
  const p2 = points[Math.min(n, idx + 1)];
  const p3 = points[Math.min(n, idx + 2)];
  const u2 = u * u;
  const u3 = u2 * u;
  const outX =
    0.5 *
    (2 * p1[0] +
      (-p0[0] + p2[0]) * u +
      (2 * p0[0] - 5 * p1[0] + 4 * p2[0] - p3[0]) * u2 +
      (-p0[0] + 3 * p1[0] - 3 * p2[0] + p3[0]) * u3);
  const outY =
    0.5 *
    (2 * p1[1] +
      (-p0[1] + p2[1]) * u +
      (2 * p0[1] - 5 * p1[1] + 4 * p2[1] - p3[1]) * u2 +
      (-p0[1] + 3 * p1[1] - 3 * p2[1] + p3[1]) * u3);
  return [outX, outY];
}

export const TRAJECTORY_CONFIGS = Object.freeze({
  'cardioid-tour': {
    id: 'cardioid-tour',
    loopMode: 'wrap',
    mandelbrotXRange: [-1.35, 0.55],
    mandelbrotYRange: [-0.95, 0.95],
    juliaSpan: 3.1,
    gScale: 7.5,
    phaseOffset: 0.08,
    demStrength: 0.15,
    sampleC(t) {
      const theta = 2 * Math.PI * ((t % 1 + 1) % 1);
      const r = 0.985;
      const cx = 0.5 * r * Math.cos(theta) - 0.25 * r * r * Math.cos(2 * theta);
      const cy = 0.5 * r * Math.sin(theta) - 0.25 * r * r * Math.sin(2 * theta);
      return [cx, cy];
    },
    waypoints: [
      { id: 'cusp', label: 'Cauliflower Cusp', t: 0.0 },
      { id: 'rabbit', label: 'Rabbit Wake (p=3)', t: 0.333 },
      { id: 'basilica', label: 'Basilica Neck (p=2)', t: 0.5 },
      { id: 'siegel', label: 'Golden Siegel Spiral', t: 0.618 },
    ],
  },
  'bifurcation-pinch': {
    id: 'bifurcation-pinch',
    loopMode: 'pingpong',
    mandelbrotXRange: [-1.52, -0.32],
    mandelbrotYRange: [-0.6, 0.6],
    juliaSpan: 3.1,
    gScale: 8.5,
    phaseOffset: 0.14,
    demStrength: 0.18,
    controlPoints: [
      [-0.52, 0.008],
      [-0.75, 0.006],
      [-1.0, 0.005],
      [-1.31, 0.004],
    ],
    sampleC(t) {
      return interpolateCatmullRom(this.controlPoints, t);
    },
    waypoints: [
      { id: 'cardioid', label: 'Single Basin', t: 0.0 },
      { id: 'neck', label: 'Parabolic Neck (c = −0.75)', t: 0.333 },
      { id: 'basilica', label: 'Basilica (p=2)', t: 0.667 },
      { id: 'period4', label: 'Period-4 Cascade', t: 1.0 },
    ],
  },
  'dendrite-lightning': {
    id: 'dendrite-lightning',
    loopMode: 'pingpong',
    mandelbrotXRange: [-0.62, 0.48],
    mandelbrotYRange: [0.45, 1.15],
    juliaSpan: 3.1,
    gScale: 9.0,
    phaseOffset: 0.0,
    demStrength: 0.35,
    controlPoints: [
      [-0.122561, 0.744862],
      [-0.1011, 0.8385],
      [-0.044, 0.932],
      [0.0, 1.0],
    ],
    sampleC(t) {
      return interpolateCatmullRom(this.controlPoints, t);
    },
    waypoints: [
      { id: 'rabbit', label: 'Douady’s Rabbit', t: 0.0 },
      { id: 'subhyperbolic', label: 'Sub-hyperbolic Neck', t: 0.333 },
      { id: 'antenna', label: 'Antenna Filament', t: 0.667 },
      { id: 'misiurewicz', label: 'Lightning Tip (c = i)', t: 1.0 },
    ],
  },
  'fatou-dust-transition': {
    id: 'fatou-dust-transition',
    loopMode: 'pingpong',
    mandelbrotXRange: [-1.02, -0.58],
    mandelbrotYRange: [-0.02, 0.34],
    juliaSpan: 3.1,
    gScale: 7.5,
    phaseOffset: 0.25,
    demStrength: 0.22,
    controlPoints: [
      [-0.745, 0.105],
      [-0.775684, 0.136467],
      [-0.8, 0.156],
      [-0.805, 0.225],
    ],
    sampleC(t) {
      return interpolateCatmullRom(this.controlPoints, t);
    },
    waypoints: [
      { id: 'connected', label: 'Connected Web', t: 0.0 },
      { id: 'boundary', label: 'Critical Boundary', t: 0.333 },
      { id: 'dragon', label: 'Dragon Spiral', t: 0.667 },
      { id: 'dust', label: 'Fatou Cantor Dust', t: 1.0 },
    ],
  },
});

export const SELF_SIMILAR_HUB_PRESETS = Object.freeze([
  {
    id: 'dragon',
    label: '2-arm Dragon',
    c: [-0.8, 0.156],
    arms: 2,
    baseSpan: 0.0028,
    gScale: 6.5,
    phaseOffset: 0.32,
  },
  {
    id: 'seahorse',
    label: '2-arm Seahorse',
    c: [-0.77568377, 0.13646737],
    arms: 2,
    baseSpan: 0.0018,
    gScale: 12.5,
    phaseOffset: 0.08,
  },
  {
    id: 'triple',
    label: '3-arm Rabbit',
    c: [-0.087593732, 0.65509028],
    arms: 3,
    baseSpan: 0.0036,
    gScale: 7.0,
    phaseOffset: 0.0,
  },
  {
    id: 'quad',
    label: '4-arm Pinwheel',
    c: [0.28245, 0.4831],
    arms: 4,
    baseSpan: 0.0032,
    gScale: 6.0,
    phaseOffset: 0.5,
  },
]);

// Shared single-context offscreen WebGL renderer so all animated subsections
// share one GPU program without competing with main FractalCanvas contexts.
let sharedGLState = null;
const mandelbrotBackdropCache = new Map();

const ANIM_VERTEX_SHADER = `
  attribute vec2 a_position;
  varying highp vec2 v_uv;
  void main() {
    v_uv = (a_position + 1.0) * 0.5;
    gl_Position = vec4(a_position, 0.0, 1.0);
  }
`;

const ANIM_FRAGMENT_SHADER = `
  precision highp float;
  varying highp vec2 v_uv;
  uniform vec2 u_center;
  uniform vec2 u_basisX;
  uniform vec2 u_basisY;
  uniform vec2 u_juliaC;
  uniform float u_isJulia;
  uniform vec2 u_koenigsA2;
  uniform vec2 u_koenigsA3;
  uniform float u_useKoenigs;
  uniform float u_muOffset;
  uniform float u_gScale;
  uniform float u_phaseOffset;
  uniform float u_demStrength;
  uniform vec3 u_trap;
  uniform sampler2D u_palette;

  vec2 cmul(vec2 a, vec2 b) {
    return vec2(a.x * b.x - a.y * b.y, a.x * b.y + a.y * b.x);
  }

  void main() {
    vec2 w = (v_uv.x - 0.5) * u_basisX + (v_uv.y - 0.5) * u_basisY;
    vec2 z0 = u_center + w;
    if (u_useKoenigs > 0.5) {
      vec2 w2 = cmul(w, w);
      vec2 w3 = cmul(w2, w);
      z0 = u_center + w + cmul(u_koenigsA2, w2) + cmul(u_koenigsA3, w3);
    }
    vec2 c = u_isJulia > 0.5 ? u_juliaC : z0;
    vec2 z = u_isJulia > 0.5 ? z0 : vec2(0.0);
    vec2 dz = vec2(u_isJulia > 0.5 ? 1.0 : 0.0, 0.0);
    float escaped = 0.0;
    float iterCount = 0.0;
    vec3 bg = vec3(10.0, 14.0, 24.0) / 255.0;

    for (int i = 0; i < 384; i++) {
      float magSq = dot(z, z);
      if (magSq > 4.0) {
        escaped = 1.0;
        iterCount = float(i);
        break;
      }
      if (u_isJulia > 0.5 && u_trap.z > 0.0 && i > 4) {
        vec2 d1 = z - u_trap.xy;
        vec2 d2 = z + u_trap.xy;
        if (dot(d1, d1) <= u_trap.z || dot(d2, d2) <= u_trap.z) {
          gl_FragColor = vec4(bg, 1.0);
          return;
        }
      }
      dz = 2.0 * cmul(z, dz) + vec2(u_isJulia > 0.5 ? 0.0 : 1.0, 0.0);
      z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    }

    if (escaped < 0.5) {
      gl_FragColor = vec4(bg, 1.0);
      return;
    }

    for (int k = 0; k < 3; k++) {
      dz = 2.0 * cmul(z, dz) + vec2(u_isJulia > 0.5 ? 0.0 : 1.0, 0.0);
      z = vec2(z.x * z.x - z.y * z.y, 2.0 * z.x * z.y) + c;
    }
    float magSq = max(dot(z, z), 1.0001);
    float mu = iterCount + 4.0 - log2(0.5 * log(magSq)) + u_muOffset;
    float safeMu = max(0.0, mu);
    float phase = (sqrt(safeMu + 1.0) - 1.0) * u_gScale / 55.0 + u_phaseOffset;
    vec3 color = texture2D(u_palette, vec2(fract(phase) + 0.5 / 512.0, 0.5)).rgb;
    if (u_demStrength > 0.0) {
      float dzMag = length(dz);
      float r = sqrt(magSq);
      if (dzMag > 0.0) {
        float dem = 0.5 * r * log(r) / dzMag;
        float pixelScale = length(u_basisY) / 360.0;
        float edge = clamp(1.0 - dem / max(pixelScale * 1.6, 1e-7), 0.0, 1.0);
        color = mix(color, vec3(248.0, 242.0, 232.0) / 255.0, edge * u_demStrength);
      }
    }
    gl_FragColor = vec4(color, 1.0);
  }
`;

function getSharedGL() {
  if (typeof document === 'undefined') return null;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '')) return null;
  if (sharedGLState && !sharedGLState.gl.isContextLost?.()) return sharedGLState;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = PAIR_CANVAS_SIZE;
    canvas.height = PAIR_CANVAS_SIZE;
    const gl = canvas.getContext('webgl', { alpha: false, antialias: false, preserveDrawingBuffer: true });
    if (!gl) return null;

    const compile = (type, src) => {
      const shader = gl.createShader(type);
      gl.shaderSource(shader, src);
      gl.compileShader(shader);
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        gl.deleteShader(shader);
        return null;
      }
      return shader;
    };
    const vs = compile(gl.VERTEX_SHADER, ANIM_VERTEX_SHADER);
    const fs = compile(gl.FRAGMENT_SHADER, ANIM_FRAGMENT_SHADER);
    if (!vs || !fs) return null;
    const program = gl.createProgram();
    gl.attachShader(program, vs);
    gl.attachShader(program, fs);
    gl.linkProgram(program);
    gl.deleteShader(vs);
    gl.deleteShader(fs);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) return null;

    const buffer = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);

    const paletteTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, paletteTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.REPEAT);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    const uniformNames = [
      'u_center', 'u_basisX', 'u_basisY', 'u_juliaC', 'u_isJulia',
      'u_koenigsA2', 'u_koenigsA3', 'u_useKoenigs', 'u_muOffset',
      'u_gScale', 'u_phaseOffset', 'u_demStrength', 'u_trap', 'u_palette',
    ];
    const uniforms = {};
    for (const name of uniformNames) uniforms[name] = gl.getUniformLocation(program, name);
    const aPosition = gl.getAttribLocation(program, 'a_position');

    sharedGLState = { canvas, gl, program, buffer, paletteTexture, uniforms, aPosition, uploadedTheme: null };
    return sharedGLState;
  } catch {
    return null;
  }
}

export function renderFractalAnimationFrame(targetCanvas, options) {
  if (!targetCanvas) return false;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !targetCanvas.getContext?.mock) {
    return false;
  }
  const {
    center = [0, 0],
    basisX = [3.1, 0],
    basisY = [0, 3.1],
    juliaC = null,
    isJulia = true,
    koenigsA2 = [0, 0],
    koenigsA3 = [0, 0],
    useKoenigs = false,
    muOffset = 0,
    gScale = 7.5,
    phaseOffset = 0,
    demStrength = 0,
    paletteTheme = 'amethyst',
  } = options;

  const w = targetCanvas.width || PAIR_CANVAS_SIZE;
  const h = targetCanvas.height || PAIR_CANVAS_SIZE;
  const ctx = targetCanvas.getContext?.('2d');
  if (!ctx) return false;

  const shared = getSharedGL();
  if (shared && typeof ctx.drawImage === 'function') {
    const { canvas: glCanvas, gl, program, buffer, paletteTexture, uniforms, aPosition } = shared;
    if (glCanvas.width !== w || glCanvas.height !== h) {
      glCanvas.width = w;
      glCanvas.height = h;
    }
    gl.viewport(0, 0, w, h);
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.enableVertexAttribArray(aPosition);
    gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, paletteTexture);
    if (shared.uploadedTheme !== paletteTheme) {
      const table = getPaletteTable(paletteTheme);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, PALETTE_SIZE, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, table);
      shared.uploadedTheme = paletteTheme;
    }

    const cycle = isJulia && juliaC ? findJuliaAttractingCycle(juliaC) : null;
    const trap = cycle?.primaryTrap;

    gl.uniform2f(uniforms.u_center, center[0], center[1]);
    gl.uniform2f(uniforms.u_basisX, basisX[0], basisX[1]);
    gl.uniform2f(uniforms.u_basisY, basisY[0], basisY[1]);
    gl.uniform2f(uniforms.u_juliaC, juliaC ? juliaC[0] : 0, juliaC ? juliaC[1] : 0);
    gl.uniform1f(uniforms.u_isJulia, isJulia ? 1 : 0);
    gl.uniform2f(uniforms.u_koenigsA2, koenigsA2[0], koenigsA2[1]);
    gl.uniform2f(uniforms.u_koenigsA3, koenigsA3[0], koenigsA3[1]);
    gl.uniform1f(uniforms.u_useKoenigs, useKoenigs ? 1 : 0);
    gl.uniform1f(uniforms.u_muOffset, muOffset);
    gl.uniform1f(uniforms.u_gScale, gScale);
    gl.uniform1f(uniforms.u_phaseOffset, phaseOffset);
    gl.uniform1f(uniforms.u_demStrength, demStrength);
    gl.uniform3f(uniforms.u_trap, trap ? trap.x : 0, trap ? trap.y : 0, trap ? trap.radiusSq : 0);
    gl.uniform1i(uniforms.u_palette, 0);

    gl.drawArrays(gl.TRIANGLES, 0, 6);
    ctx.drawImage(glCanvas, 0, 0, w, h);
    return true;
  }

  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !targetCanvas.getContext?.mock) {
    return false;
  }
  if (typeof ctx.createImageData !== 'function') return false;

  const imageData = ctx.createImageData(w, h);
  if (!imageData?.data) return false;
  const data32 = new Uint32Array(imageData.data.buffer);
  const palette = withPaletteTheme({ ...EXPLORER_PALETTE, gScale, phaseOffset, juliaC }, paletteTheme);
  const iterResult = { iterations: 0, mu: 0, escaped: false };
  const rgba = [10, 14, 24, 255];
  const bgPacked = 10 | (14 << 8) | (24 << 16) | (255 << 24);

  for (let row = 0; row < h; row++) {
    const v = (row + 0.5) / h;
    const dy = 0.5 - v;
    const rowOffset = row * w;
    for (let col = 0; col < w; col++) {
      const u = (col + 0.5) / w;
      const dx = u - 0.5;
      const wx = dx * basisX[0] + dy * basisY[0];
      const wy = dx * basisX[1] + dy * basisY[1];
      let x0 = center[0] + wx;
      let y0 = center[1] + wy;
      if (useKoenigs) {
        const w2 = cmul([wx, wy], [wx, wy]);
        const w3 = cmul(w2, [wx, wy]);
        const k2 = cmul(koenigsA2, w2);
        const k3 = cmul(koenigsA3, w3);
        x0 += k2[0] + k3[0];
        y0 += k2[1] + k3[1];
      }
      iterateUntilEscape(x0, y0, iterResult, 192, true, isJulia ? juliaC : null);
      if (iterResult.escaped) {
        samplePalette(Math.max(0, iterResult.mu + muOffset), palette, rgba);
        data32[rowOffset + col] = rgba[0] | (rgba[1] << 8) | (rgba[2] << 16) | (255 << 24);
      } else {
        data32[rowOffset + col] = bgPacked;
      }
    }
  }
  ctx.putImageData(imageData, 0, 0);
  return true;
}

function computeAttractingCyclePoints([cx, cy], period) {
  if (!period || period < 1) return [];
  let zx = 0;
  let zy = 0;
  for (let i = 0; i < 96; i++) {
    const nx = zx * zx - zy * zy + cx;
    const ny = 2 * zx * zy + cy;
    if (nx * nx + ny * ny > 16) return [];
    zx = nx;
    zy = ny;
  }
  const pts = [];
  for (let p = 0; p < period; p++) {
    pts.push([zx, zy]);
    const nx = zx * zx - zy * zy + cx;
    const ny = 2 * zx * zy + cy;
    zx = nx;
    zy = ny;
  }
  return pts;
}

function drawJuliaDynamicalOverlay(canvas, currentC, koenigs, classification, span, paletteTheme) {
  if (!canvas) return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
    return;
  }
  const ctx = canvas.getContext?.('2d');
  if (!ctx || typeof ctx.beginPath !== 'function') return;
  const w = canvas.width || PAIR_CANVAS_SIZE;
  const h = canvas.height || PAIR_CANVAS_SIZE;
  const toScreen = ([zx, zy]) => [
    (0.5 + zx / span) * w,
    (0.5 - zy / span) * h,
  ];
  const accent = paletteTheme === 'celadon' ? 'rgba(167, 236, 217, 0.85)' : 'rgba(246, 201, 146, 0.85)';
  const subtleStroke = paletteTheme === 'celadon' ? 'rgba(167, 236, 217, 0.42)' : 'rgba(246, 201, 146, 0.42)';

  ctx.save();
  if (classification.period && classification.period >= 1) {
    const cyclePts = computeAttractingCyclePoints(currentC, classification.period);
    if (cyclePts.length > 1) {
      ctx.beginPath();
      cyclePts.forEach((pt, idx) => {
        const [sx, sy] = toScreen(pt);
        if (idx === 0) ctx.moveTo(sx, sy);
        else ctx.lineTo(sx, sy);
      });
      ctx.closePath();
      ctx.strokeStyle = subtleStroke;
      ctx.lineWidth = 1.25;
      ctx.stroke();
    }
    for (const pt of cyclePts) {
      const [sx, sy] = toScreen(pt);
      ctx.beginPath();
      ctx.arc(sx, sy, 3, 0, 2 * Math.PI);
      ctx.fillStyle = accent;
      ctx.fill();
    }
  } else {
    let zx = 0;
    let zy = 0;
    ctx.beginPath();
    for (let i = 0; i < 8; i++) {
      const [sx, sy] = toScreen([zx, zy]);
      if (i === 0) ctx.moveTo(sx, sy);
      else ctx.lineTo(sx, sy);
      const nx = zx * zx - zy * zy + currentC[0];
      const ny = 2 * zx * zy + currentC[1];
      zx = nx;
      zy = ny;
      if (zx * zx + zy * zy > 6) break;
    }
    ctx.strokeStyle = subtleStroke;
    ctx.lineWidth = 1.15;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  for (const sign of [1, -1]) {
    const [ax, ay] = toScreen([sign * koenigs.alpha[0], sign * koenigs.alpha[1]]);
    ctx.beginPath();
    ctx.arc(ax, ay, 3.8, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(10, 14, 24, 0.75)';
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.82)';
    ctx.stroke();
  }
  ctx.restore();
}

function renderMandelbrotTrajectoryMap(canvas, config, currentT, currentC, paletteTheme) {
  if (!canvas) return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
    return;
  }
  const ctx = canvas.getContext?.('2d');
  if (!ctx) return;
  const w = canvas.width || PAIR_CANVAS_SIZE;
  const h = canvas.height || PAIR_CANVAS_SIZE;
  const [xMin, xMax] = config.mandelbrotXRange;
  const [yMin, yMax] = config.mandelbrotYRange;
  const cacheKey = `${config.id}:${w}x${h}:${paletteTheme}`;

  let backdrop = mandelbrotBackdropCache.get(cacheKey);
  if (!backdrop && typeof document !== 'undefined') {
    const offscreen = document.createElement('canvas');
    offscreen.width = w;
    offscreen.height = h;
    const rendered = renderFractalAnimationFrame(offscreen, {
      center: [(xMin + xMax) / 2, (yMin + yMax) / 2],
      basisX: [xMax - xMin, 0],
      basisY: [0, yMax - yMin],
      isJulia: false,
      gScale: 7.0,
      phaseOffset: 0,
      paletteTheme,
    });
    if (rendered) {
      backdrop = offscreen;
      mandelbrotBackdropCache.set(cacheKey, offscreen);
    }
  }
  if (backdrop && typeof ctx.drawImage === 'function') {
    ctx.drawImage(backdrop, 0, 0, w, h);
  } else if (typeof ctx.fillRect === 'function') {
    ctx.fillStyle = '#0a0e18';
    ctx.fillRect(0, 0, w, h);
  }

  if (typeof ctx.beginPath !== 'function') return;
  const toScreen = ([cx, cy]) => [
    ((cx - xMin) / (xMax - xMin)) * w,
    ((yMax - cy) / (yMax - yMin)) * h,
  ];

  // Draw parametric trajectory curve c(s)
  ctx.save();
  ctx.beginPath();
  const samples = 96;
  for (let i = 0; i <= samples; i++) {
    const s = i / samples;
    const [sx, sy] = toScreen(config.sampleC(s));
    if (i === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  }
  ctx.strokeStyle = paletteTheme === 'celadon' ? 'rgba(139, 219, 198, 0.78)' : 'rgba(246, 201, 146, 0.78)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Draw waypoint markers
  for (const wp of config.waypoints) {
    const [wx, wy] = toScreen(config.sampleC(wp.t));
    ctx.beginPath();
    ctx.arc(wx, wy, 3.5, 0, 2 * Math.PI);
    ctx.fillStyle = 'rgba(10, 14, 24, 0.9)';
    ctx.fill();
    ctx.lineWidth = 1.5;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
    ctx.stroke();
  }

  // Draw active cursor c(t)
  const [px, py] = toScreen(currentC);
  ctx.beginPath();
  ctx.arc(px, py, 6, 0, 2 * Math.PI);
  ctx.fillStyle = paletteTheme === 'celadon' ? '#e7f8f2' : '#fff0db';
  ctx.fill();
  ctx.lineWidth = 2;
  ctx.strokeStyle = '#0a0e18';
  ctx.stroke();
  ctx.restore();
}

function drawSelfSimilarSpiralOverlay(canvas, koenigs, cameraBasis, phase, paletteTheme) {
  if (!canvas) return;
  if (typeof navigator !== 'undefined' && /jsdom/i.test(navigator.userAgent || '') && !canvas.getContext?.mock) {
    return;
  }
  const ctx = canvas.getContext?.('2d');
  if (!ctx || typeof ctx.beginPath !== 'function') return;
  const w = canvas.width || ZOOM_CANVAS_WIDTH;
  const h = canvas.height || ZOOM_CANVAS_HEIGHT;
  const cx = w / 2;
  const cy = h / 2;
  const accent = paletteTheme === 'celadon' ? 'rgba(167, 236, 217, 0.72)' : 'rgba(246, 201, 146, 0.72)';

  ctx.save();
  const baseRadius = Math.min(w, h) * 0.3;
  const ringRadius = baseRadius * Math.pow(koenigs.magM, -(phase % 1));
  ctx.beginPath();
  ctx.arc(cx, cy, ringRadius, 0, 2 * Math.PI);
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.24)';
  ctx.lineWidth = 1.25;
  ctx.setLineDash([5, 5]);
  ctx.stroke();
  ctx.setLineDash([]);

  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, 2 * Math.PI);
  ctx.fillStyle = accent;
  ctx.fill();
  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#0a0e18';
  ctx.stroke();
  ctx.restore();
}

function formatSignedImag(value, digits = 4) {
  const sign = value >= 0 ? '+' : '\u2212';
  return `${sign} ${Math.abs(value).toFixed(digits)}i`;
}

function nearestWaypoint(waypoints, t) {
  let best = waypoints[0];
  let bestDist = Infinity;
  for (const wp of waypoints) {
    const d = Math.abs(wp.t - t);
    if (d < bestDist) {
      bestDist = d;
      best = wp;
    }
  }
  return best;
}

function nearestPhaseOnTrajectory(config, targetC) {
  let bestT = 0;
  let bestDistSq = Infinity;
  const steps = 160;
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const [cx, cy] = config.sampleC(t);
    const dx = cx - targetC[0];
    const dy = cy - targetC[1];
    const distSq = dx * dx + dy * dy;
    if (distSq < bestDistSq) {
      bestDistSq = distSq;
      bestT = t;
    }
  }
  return Number(bestT.toFixed(4));
}

const PlayPauseIcon = ({ playing }) => (
  <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true" className="ciechanow-play-icon">
    {playing ? (
      <>
        <rect x="4.5" y="3.5" width="3.8" height="13" rx="1" fill="currentColor" />
        <rect x="11.7" y="3.5" width="3.8" height="13" rx="1" fill="currentColor" />
      </>
    ) : (
      <path d="M5.5 3.5L16 10L5.5 16.5V3.5Z" fill="currentColor" />
    )}
  </svg>
);

export function JuliaTrajectorySubsection({ entry, paletteTheme = 'amethyst' }) {
  const { section, headingId, title, description } = entry;
  const config = TRAJECTORY_CONFIGS[section] || TRAJECTORY_CONFIGS['cardioid-tour'];
  const sectionRef = useRef(null);
  const juliaCanvasRef = useRef(null);
  const mandelbrotCanvasRef = useRef(null);
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [inView, setInView] = useState(false);
  const [draggingMap, setDraggingMap] = useState(false);
  const directionRef = useRef(1);

  const currentC = useMemo(() => config.sampleC(phase), [config, phase]);
  const koenigs = useMemo(() => computeKoenigsInvariants(currentC, 2), [currentC]);
  const classification = useMemo(() => classifyParameterC(currentC), [currentC]);
  const activeWaypoint = useMemo(() => nearestWaypoint(config.waypoints, phase), [config.waypoints, phase]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      entries => {
        for (const item of entries) {
          setInView(item.isIntersecting);
        }
      },
      { rootMargin: '120px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'undefined' && !inView) return;
    renderFractalAnimationFrame(juliaCanvasRef.current, {
      center: [0, 0],
      basisX: [config.juliaSpan, 0],
      basisY: [0, config.juliaSpan],
      juliaC: currentC,
      isJulia: true,
      gScale: config.gScale,
      phaseOffset: config.phaseOffset,
      demStrength: config.demStrength,
      paletteTheme,
    });
    drawJuliaDynamicalOverlay(juliaCanvasRef.current, currentC, koenigs, classification, config.juliaSpan, paletteTheme);
    renderMandelbrotTrajectoryMap(mandelbrotCanvasRef.current, config, phase, currentC, paletteTheme);
  }, [classification, config, currentC, inView, koenigs, paletteTheme, phase]);

  useEffect(() => {
    if (!playing || !inView) return undefined;
    let frameId = null;
    let lastTime = null;
    const tick = timestamp => {
      const dt = lastTime === null ? 16 : Math.min(50, timestamp - lastTime);
      lastTime = timestamp;
      const delta = (dt / 1000) * 0.075;
      setPhase(prev => {
        if (config.loopMode === 'wrap') {
          return Number(((prev + delta) % 1).toFixed(4));
        }
        let next = prev + delta * directionRef.current;
        if (next >= 1) {
          next = 1;
          directionRef.current = -1;
        } else if (next <= 0) {
          next = 0;
          directionRef.current = 1;
        }
        return Number(next.toFixed(4));
      });
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [config.loopMode, inView, playing]);

  const scrubFromMandelbrotEvent = useCallback(
    event => {
      const canvas = mandelbrotCanvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      const u = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
      const v = Math.max(0, Math.min(1, (event.clientY - rect.top) / rect.height));
      const [xMin, xMax] = config.mandelbrotXRange;
      const [yMin, yMax] = config.mandelbrotYRange;
      const targetC = [xMin + u * (xMax - xMin), yMax - v * (yMax - yMin)];
      setPlaying(false);
      setPhase(nearestPhaseOnTrajectory(config, targetC));
    },
    [config],
  );

  return (
    <section
      ref={sectionRef}
      id={section}
      className="subsection fractal-subsection trajectory-morph-subsection"
      data-section={section}
      data-testid={`trajectory-morph-${section}`}
      data-palette-theme={paletteTheme}
      aria-labelledby={headingId}
    >
      <h4 id={headingId}>{title}</h4>
      {section === 'cardioid-tour' ? (
        <p>
          Sweeping c(&theta;) = &frac12;re<sup>i&theta;</sup> &minus; &frac14;r&sup2;e<sup>2i&theta;</sup>{' '}
          around the main cardioid boundary rotates the fixed-point multiplier{' '}
          &lambda; = re<sup>i&theta;</sup> through a full turn, morphing the filled
          Julia set from the cusped cauliflower through three-eared rabbit buds,
          pinched basilica lobes, and golden-mean Siegel spirals.
        </p>
      ) : (
        <p>{description}</p>
      )}

      <div className="ciechanow-figure">
        <div className="ciechanow-pair-grid">
          <div className="ciechanow-stage">
            <canvas
              ref={juliaCanvasRef}
              width={PAIR_CANVAS_SIZE}
              height={PAIR_CANVAS_SIZE}
              aria-label={`${title} animated Julia set`}
            />
            <span className="ciechanow-corner-badge ciechanow-corner-top">
              z &middot; <span data-testid={`trajectory-milestone-${section}`}>{activeWaypoint.label}</span>
            </span>
            <span
              className="ciechanow-sr-only"
              data-testid={`trajectory-regime-${section}`}
            >
              {classification.label}
            </span>
          </div>

          <div
            className="ciechanow-stage ciechanow-stage-interactive"
            onMouseDown={event => {
              setDraggingMap(true);
              scrubFromMandelbrotEvent(event);
            }}
            onMouseMove={event => {
              if (draggingMap) scrubFromMandelbrotEvent(event);
            }}
            onMouseUp={() => setDraggingMap(false)}
            onMouseLeave={() => setDraggingMap(false)}
          >
            <canvas
              ref={mandelbrotCanvasRef}
              width={PAIR_CANVAS_SIZE}
              height={PAIR_CANVAS_SIZE}
              aria-label={`${title} Mandelbrot parameter trajectory`}
            />
            <span
              className="ciechanow-corner-badge ciechanow-corner-top"
              data-testid={`trajectory-c-readout-${section}`}
            >
              c = {currentC[0].toFixed(4)} {formatSignedImag(currentC[1], 4)}
            </span>
          </div>
        </div>

        <div className="ciechanow-scrubber-row">
          <button
            type="button"
            className={`ciechanow-play-button${playing ? ' active' : ''}`}
            data-testid={`trajectory-play-toggle-${section}`}
            aria-label={playing ? `Pause ${title}` : `Play ${title}`}
            aria-pressed={playing}
            onClick={() => setPlaying(prev => !prev)}
          >
            <PlayPauseIcon playing={playing} />
          </button>

          <div className="ciechanow-slider-stack">
            <input
              type="range"
              className="ciechanow-range"
              min="0"
              max="1"
              step="0.005"
              aria-label={`${title} phase`}
              value={phase}
              onChange={event => {
                setPlaying(false);
                setPhase(Number(event.target.value));
              }}
            />
            <div className="ciechanow-ticks" role="group" aria-label={`${title} milestones`}>
              {config.waypoints.map(wp => {
                const isCurrent = Math.abs(phase - wp.t) < 0.08;
                return (
                  <button
                    key={wp.id}
                    type="button"
                    className={`ciechanow-tick-button${isCurrent ? ' active' : ''}`}
                    data-waypoint-id={wp.id}
                    onClick={() => {
                      setPlaying(false);
                      setPhase(wp.t);
                    }}
                  >
                    {wp.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function SelfSimilarZoomSubsection({ entry, paletteTheme = 'amethyst' }) {
  const { section, headingId, title, description } = entry;
  const sectionRef = useRef(null);
  const canvasRef = useRef(null);
  const [hubId, setHubId] = useState(SELF_SIMILAR_HUB_PRESETS[0].id);
  const [zoomMode, setZoomMode] = useState('eigen');
  const [phase, setPhase] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [inView, setInView] = useState(false);

  const hub = useMemo(
    () => SELF_SIMILAR_HUB_PRESETS.find(item => item.id === hubId) || SELF_SIMILAR_HUB_PRESETS[0],
    [hubId],
  );
  const koenigs = useMemo(() => computeKoenigsInvariants(hub.c, hub.arms), [hub]);

  const cameraBasis = useMemo(() => {
    const aspect = ZOOM_CANVAS_WIDTH / ZOOM_CANVAS_HEIGHT;
    if (zoomMode === 'eigen') {
      const scale = Math.pow(koenigs.magM, -phase);
      const angle = -phase * koenigs.argM;
      const span = hub.baseSpan * scale;
      const spanX = span * aspect;
      const cosA = Math.cos(angle);
      const sinA = Math.sin(angle);
      return {
        span,
        scaleFactor: 1 / scale,
        rotationDeg: (angle * 180) / Math.PI,
        basisX: [spanX * cosA, spanX * sinA],
        basisY: [-span * sinA, span * cosA],
        muOffset: -hub.arms * phase,
        useKoenigs: true,
      };
    }
    // Axis-aligned multi-period zoom across 6 arm-return periods
    const periods = 6;
    const scale = Math.pow(koenigs.magM, -periods * phase);
    const span = hub.baseSpan * scale;
    return {
      span,
      scaleFactor: 1 / scale,
      rotationDeg: 0,
      basisX: [span * aspect, 0],
      basisY: [0, span],
      muOffset: -hub.arms * periods * phase,
      useKoenigs: false,
    };
  }, [hub, koenigs, phase, zoomMode]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(
      entries => {
        for (const item of entries) {
          setInView(item.isIntersecting);
        }
      },
      { rootMargin: '120px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (typeof IntersectionObserver !== 'undefined' && !inView) return;
    renderFractalAnimationFrame(canvasRef.current, {
      center: koenigs.alpha,
      basisX: cameraBasis.basisX,
      basisY: cameraBasis.basisY,
      juliaC: hub.c,
      isJulia: true,
      koenigsA2: koenigs.a2,
      koenigsA3: koenigs.a3,
      useKoenigs: cameraBasis.useKoenigs,
      muOffset: cameraBasis.muOffset,
      gScale: hub.gScale,
      phaseOffset: hub.phaseOffset,
      demStrength: 0.2,
      paletteTheme,
    });
    drawSelfSimilarSpiralOverlay(canvasRef.current, koenigs, cameraBasis, phase, paletteTheme);
  }, [cameraBasis, hub, inView, koenigs, paletteTheme, phase]);

  useEffect(() => {
    if (!playing || !inView) return undefined;
    let frameId = null;
    let lastTime = null;
    const tick = timestamp => {
      const dt = lastTime === null ? 16 : Math.min(50, timestamp - lastTime);
      lastTime = timestamp;
      const delta = (dt / 1000) * 0.22;
      setPhase(prev => Number(((prev + delta) % 1).toFixed(4)));
      frameId = requestAnimationFrame(tick);
    };
    frameId = requestAnimationFrame(tick);
    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
    };
  }, [inView, playing]);

  const handleSelectHub = useCallback(nextId => {
    setHubId(nextId);
    setPhase(0);
  }, []);

  return (
    <section
      ref={sectionRef}
      id={section}
      className="subsection fractal-subsection self-similar-zoom-subsection"
      data-section={section}
      data-testid="self-similar-zoom-explorer"
      data-palette-theme={paletteTheme}
      aria-labelledby={headingId}
    >
      <h4 id={headingId}>{title}</h4>
      {section === 'koenigs-self-similar-zoom' ? (
        <p>
          Near any repelling fixed point &alpha;<sub>c</sub> = (1 &minus; &radic;(1 &minus; 4c))/2
          {' '}with multiplier &lambda; = 2&alpha;<sub>c</sub> (|&lambda;| &gt; 1),
          iterating f<sub>c</sub><sup>&circ;m</sup> expands each spiral arm onto
          itself by &lambda;<sup>m</sup>. Applying the cubic inverse Koenigs chart{' '}
          &psi;(w) = &alpha;<sub>c</sub> + w + a<sub>2</sub>w&sup2; + a<sub>3</sub>w&sup3;{' '}
          while scaling by |&lambda;|<sup>&minus;mt</sup>, counter-rotating by{' '}
          &minus;t&middot;arg(&lambda;<sup>m</sup>), and shifting smooth escape
          counts by &minus;mt loops a single shallow octave seamlessly forever.
        </p>
      ) : (
        <p>{description}</p>
      )}

      <div className="ciechanow-figure">
        <div className="ciechanow-stage ciechanow-stage-wide">
          <canvas
            ref={canvasRef}
            width={ZOOM_CANVAS_WIDTH}
            height={ZOOM_CANVAS_HEIGHT}
            aria-label="Continuous self-similar Julia spiral zoom loop"
          />
          <span
            className="ciechanow-corner-badge ciechanow-corner-top"
            data-testid="self-similar-readout"
          >
            &alpha;<sub>c</sub> = {koenigs.alpha[0].toFixed(4)} {formatSignedImag(koenigs.alpha[1], 4)} &middot; |&lambda;|<sup>{hub.arms}</sup> = {koenigs.magM.toFixed(3)}&times;
          </span>
          <span
            className="ciechanow-sr-only"
            data-testid="self-similar-telemetry"
          >
            c = {hub.c[0].toFixed(4)} {formatSignedImag(hub.c[1], 4)} &middot; {hub.arms}-step return ({koenigs.argMDeg.toFixed(1)}&deg; twist) &middot; span {cameraBasis.span.toExponential(2)}
          </span>
        </div>

        <div className="ciechanow-scrubber-row">
          <button
            type="button"
            className={`ciechanow-play-button${playing ? ' active' : ''}`}
            data-testid="self-similar-play-toggle"
            aria-label={playing ? 'Pause self-similar zoom loop' : 'Play self-similar zoom loop'}
            aria-pressed={playing}
            onClick={() => setPlaying(prev => !prev)}
          >
            <PlayPauseIcon playing={playing} />
          </button>

          <div className="ciechanow-slider-stack">
            <input
              type="range"
              className="ciechanow-range"
              min="0"
              max="0.995"
              step="0.005"
              data-testid="self-similar-phase-slider"
              aria-label="Self-similar zoom octave phase"
              value={phase}
              onChange={event => {
                setPlaying(false);
                setPhase(Number(event.target.value));
              }}
            />
            <div className="ciechanow-ticks" role="group" aria-label="Repelling fixed-point spiral hubs">
              {SELF_SIMILAR_HUB_PRESETS.map(preset => (
                <button
                  key={preset.id}
                  type="button"
                  className={`ciechanow-tick-button${hub.id === preset.id ? ' active' : ''}`}
                  data-hub-preset={preset.id}
                  onClick={() => handleSelectHub(preset.id)}
                >
                  {preset.label}
                </button>
              ))}
              <button
                type="button"
                className={`ciechanow-tick-button${zoomMode === 'axis' ? ' active' : ''}`}
                data-zoom-mode={zoomMode === 'eigen' ? 'axis' : 'eigen'}
                onClick={() => setZoomMode(prev => (prev === 'eigen' ? 'axis' : 'eigen'))}
              >
                {zoomMode === 'eigen' ? 'Eigen-frame' : 'Axis-aligned'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
